import { IconButton, Stack, Tooltip } from '@chronosphereio/chrono-ui';
import { produce } from 'immer';
import { Plus as PlusIcon } from 'mdi-material-ui';
import { useEffect, useState } from 'react';
import { TableSort as TableSortOptions } from '@/core';
import { OptionsEditorGroup } from '@/plugin-editor-components';
import { TableSort } from './TableSort';

export interface TableSortEditorProps {
  sort?: TableSortOptions[];
  onChange: (sort?: TableSortOptions[]) => void;
  options: Array<{ id: string; label: string }>;
}

export function TableSortEditor({ sort, options, onChange }: TableSortEditorProps) {
  const [sortArray, setSortArray] = useState(sort ?? []);

  const handleTableSort = (newSort: TableSortOptions, index: number) => {
    if (sort?.length) {
      onChange(
        produce(sort, (draft) => {
          draft[index] = newSort;
        })
      );
    } else if (newSort.sortBy || newSort.sortOrder) {
      onChange([newSort]);
    } else {
      onChange(undefined);
    }
  };

  const deleteSort = (index: number) => {
    if (sort && sort[index]) {
      const newSort = produce(sort, (draft) => {
        draft.splice(index, 1);
      });
      onChange(newSort.length ? newSort : undefined);
    } else if (sortArray[index]) {
      setSortArray(sortArray.filter((sort) => sort !== sortArray[index]));
    }
  };

  const addSort = () => {
    setSortArray(
      produce(sortArray, (draft) => {
        draft.push({});
      })
    );
  };

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSortArray(sort ?? []);
  }, [sort]);

  return (
    <>
      <OptionsEditorGroup
        title="Sorting"
        icon={
          <Tooltip Content="Add sort">
            <div>
              <IconButton size="small" aria-label="add sort" disabled={!sort?.length} onClick={addSort}>
                <PlusIcon fontSize="small" />
              </IconButton>
            </div>
          </Tooltip>
        }
      >
        {sortArray.length ? (
          sortArray.map((s, i) => (
            <Stack
              key={i}
              spacing={1}
              sx={(theme) => ({
                borderBottom: i !== sortArray.length - 1 ? 1 : undefined,
                borderColor: 'divider',
                paddingBottom: theme.spacing(1),
              })}
            >
              <TableSort
                sort={s}
                onChange={(sort) => handleTableSort(sort, i)}
                sortByOptions={options}
                onDelete={() => deleteSort(i)}
              />
            </Stack>
          ))
        ) : (
          <TableSort onChange={(sort) => handleTableSort(sort, 0)} sortByOptions={options} />
        )}
      </OptionsEditorGroup>
    </>
  );
}
